import { useState, useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import apiManager from "../../api/apiManager";
import API_ENDPOINTS from "../../api/apiEndpoints";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate, useLocation } from "react-router-dom";
import { showErrorMessage } from "../../shared/Services/toasterService";
import { useLocalization } from "../../context/LocalizationContext";
import { format } from "date-fns";

const customStyles = {
  headCells: {
    style: {
      backgroundColor: "#f4f4f4",
      color: "#333",
      borderRight: "1px solid #ccc",
      fontSize: "16px",
      fontWeight: 600,
    },
  },
  cells: {
    style: {
      borderRight: "1px solid #eee",
      fontSize: "13px",
    },
  },
  rows: {
    style: {
      minHeight: "45px",
    },
  },
};

const CustomerHistory = () => {
  const { t: localization } = useLocalization();
  const [allData, setAllData] = useState([]);
  const [gridSearch, setGridSearch] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { customerId, customerName, vehicleId, vehicleName, vin } = location.state || {};
  const containerRef = useRef(null);
  const [filters, setFilters] = useState({
    fromDate: null,
    toDate: null,
  });

  const fetchCustomerHistory = async (applyFilters = false) => {
    if (!customerId) return;
    try {
      let payload = {
        customerId: customerId,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      // Add vehicleId to filter by specific vehicle
      if (vehicleId) {
        payload.vehicleId = vehicleId;
      }

      if (applyFilters) {
        let fromDateFormatted = null;
        if (filters.fromDate) {
          const date = new Date(filters.fromDate);
          date.setHours(0, 0, 0, 0);
          fromDateFormatted = date.toISOString();
        }

        let toDateFormatted = null;
        if (filters.toDate) {
          const date = new Date(filters.toDate);
          date.setHours(23, 59, 59, 999);
          toDateFormatted = date.toISOString();
        }

        payload = {
          ...payload,
          startDate: fromDateFormatted,
          endDate: toDateFormatted,
        };
      }

      const response = await apiManager.post(
        API_ENDPOINTS.REPAIR_ORDER_BY_CUSTOMER_ID,
        payload
      );
      const orders = response.data;
      
      // Filter by vehicleId on frontend as additional safety check
      const filteredOrders = vehicleId 
        ? orders.filter(order => order.vehicleId === vehicleId)
        : orders;
        
      const mappedOrders = filteredOrders.map((item, index) => ({
        ...item,
        id: index + 1,
        dateOfService: item.dateOfService
          ? format(new Date(item.dateOfService), "MM-dd-yyyy hh:mm a")
          : "",
        customerName: customerName,
      }));
      setAllData(mappedOrders);
    } catch (error) {
      showErrorMessage(error);
    }
  };

  useEffect(() => {
    fetchCustomerHistory(false);
  }, [customerId, vehicleId]);

  const applyFilters = () => {
    if (
      (filters.fromDate && !filters.toDate) ||
      (!filters.fromDate && filters.toDate)
    ) {
      showErrorMessage("Please select both from and to dates");
      return;
    }

    if (filters.fromDate && filters.toDate) {
      const from = new Date(filters.fromDate);
      const to = new Date(filters.toDate);
      if (to < from) {
        showErrorMessage("To date must be after from date");
        return;
      }
    }
    fetchCustomerHistory(true);
  };

  const clearFilters = () => {
    setFilters({
      fromDate: null,
      toDate: null,
    });
    setGridSearch("");
    fetchCustomerHistory(false);
  };

  const columns = [
    {
      name: localization("service_id"),
      selector: (row) => row.repairOrderId,
      sortable: true,
    },
    {
      name: localization("vehicle"),
      selector: (row) =>
        `${row.year || ""} ${row.make || ""} ${row.model || ""}`.trim(),
      sortable: true,
      wrap: true,
    },
    {
      name: localization("vin"),
      selector: (row) => row.vin || localization("not_available"),
      sortable: true,
      wrap: true,
    },
    {
      name: localization("dealership"),
      selector: (row) => row.dealerShipName,
      sortable: true,
      wrap: true,
    },
    {
      name: localization("date"),
      selector: (row) => row.dateOfService,
      sortable: true,
    },
    {
      name: localization("actions"),
      cell: (row) => (
        <div className="d-flex justify-content-start align-items-center gap-2">
          <button
            className="btn btn-sm me-2 text-white btn-blue"
            title={localization("view")}
            onClick={() =>
              navigate("/printHistory", {
                state: { repairOrderId: row.repairOrderId },
              })
            }
          >
            <i className="fas fa-eye fa-xs"></i>
          </button>
        </div>
      ),
      style: { justifyContent: "flex-start" },
      ignoreRowClick: true,
      allowOverflow: true,
      width: "100px",
    },
  ];

  const filteredData = allData.filter((item) => {
    const matchesSearch =
      gridSearch.trim() === "" ||
      Object.values(item).some(
        (val) =>
          val && val.toString().toLowerCase().includes(gridSearch.toLowerCase())
      );
    return matchesSearch;
  });

  return (
    <div className="container-fluid content-wrapper select-vehicle-content">
      <div className="content form-card">
        <div className="container-fluid p-0 mb-3">
          <div className="page-title">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-md-6">
                  <h1>{localization("service_history")}</h1>
                  {vehicleName && (
                    <p style={{ fontSize: "14px", color: "#666", marginTop: "5px" }}>
                      <strong>{customerName}</strong> - {vehicleName} {vin ? `(VIN: ${vin})` : ""}
                    </p>
                  )}
                </div>
                <div className="col-md-6">
                  <div className="d-flex flex-wrap justify-content-end">
                    <div className="d-flex justify-content-end">
                      <input
                        type="text"
                        placeholder={localization("search")}
                        className="form-control"
                        style={{ width: "200px" }}
                        value={gridSearch}
                        onChange={(e) => setGridSearch(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row align-items-center mb-3">
            <div className="col d-flex justify-content-start flex-wrap gap-2">
              <div ref={containerRef} style={{ display: "flex", gap: "10px" }}>
                <DatePicker
                  selected={filters.fromDate}
                  onChange={(date) =>
                    setFilters({ ...filters, fromDate: date })
                  }
                  className="form-control"
                  placeholderText={localization("from_date")}
                  dateFormat="yyyy-MM-dd"
                  maxDate={filters.toDate || new Date()}
                />
                <DatePicker
                  selected={filters.toDate}
                  onChange={(date) => setFilters({ ...filters, toDate: date })}
                  className="form-control"
                  placeholderText={localization("to_date")}
                  dateFormat="yyyy-MM-dd"
                  minDate={filters.fromDate}
                  maxDate={new Date()}
                />
              </div>
              <div className="col-md-3 d-flex gap-2">
                <button
                  className="btn btn-blue d-flex align-items-center justify-content-center gap-2"
                  onClick={applyFilters}
                  title={localization("apply_filters")}
                >
                  <i className="fas fa-filter"></i>
                </button>
                <button
                  className="btn btn-danger d-flex align-items-center justify-content-center gap-2"
                  onClick={clearFilters}
                  title={localization("clear_filters")}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>
          <div>
            <DataTable
              columns={columns}
              data={filteredData.length > 0 ? filteredData : []}
              pagination={filteredData.length > 0}
              striped
              highlightOnHover
              pointerOnHover
              noDataComponent={localization("no_record_found")}
              responsive
              customStyles={customStyles}
              noHeader
            />
            <button
              className="btn btn-blue btn-sm"
              onClick={() => navigate("/customer")}
            >
              {localization("back")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerHistory;